import { useContext } from 'react';
import type { ExecutionResult } from 'react-apollo';

import { useMutation } from '@apollo/client';
import type { DataProxy } from 'apollo-cache';

import { isAssignmentGradingInPublicEnabled } from 'bundles/grading-common';
import applyGradeOverrideMutation from 'bundles/grading/api/ApplyGradeOverrideMutation.graphql';
import applyGradeOverrideMutationV2 from 'bundles/grading/api/ApplyGradeOverrideMutationV2.graphql';
import type {
  ApplyGradeOverrideV2Mutation,
  ApplyGradeOverrideV2MutationVariables,
} from 'bundles/grading/api/__generated__/ApplyGradeOverrideMutationV2';
import type { ApplyGradeOverrideMutation, ApplyGradeOverrideMutationVariables } from 'bundles/grading/api/types';
import { ItemType } from 'bundles/grading/api/types';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import {
  generateSubmissionDataQueryVariables,
  updateCachedAssignmentGradeData,
  updateCachedAssignmentGradeDataV2,
} from 'bundles/grading/utils/apiUtils';
import {
  getNewSubmitterIdFromLegacyId,
  getScopedSubmitterIdFromRouteSubmitterId,
} from 'bundles/grading/utils/submitterUtils';

type MutationInput = {
  grade: number;
  explanation: string;
  partnerExplanation: string;
};

const useApplyGradeOverrideMutation = () => {
  const { pageData, submissionData } = useContext(SGAPageContext);
  const routeSubmitterId = submissionData.data?.submitter?.id || '';
  const useApiV2 = isAssignmentGradingInPublicEnabled();

  const onApplyGradeOverrideMutationComplete = (
    cache: DataProxy,
    { data }: ExecutionResult<ApplyGradeOverrideMutation>
  ) => {
    if (data?.GroupGrading_ApplyGradeOverride) {
      updateCachedAssignmentGradeData(
        cache,
        data,
        generateSubmissionDataQueryVariables(
          data?.GroupGrading_ApplyGradeOverride.submissionReference.groupId,
          data?.GroupGrading_ApplyGradeOverride.submissionReference.itemId,
          ItemType.StaffGradedAssignment,
          data?.GroupGrading_ApplyGradeOverride.submissionReference.submitterId
        )
      );
    }
  };

  const [mutate, { loading, error }] = useMutation<ApplyGradeOverrideMutation, ApplyGradeOverrideMutationVariables>(
    applyGradeOverrideMutation,
    {
      context: { clientName: 'gatewayGql' },
      update: onApplyGradeOverrideMutationComplete,
    }
  );

  const applyGradeOverride = ({ grade, explanation, partnerExplanation }: MutationInput) => {
    return mutate({
      variables: {
        input: {
          grade,
          explanation: explanation || null,
          partnerExplanation: partnerExplanation || null,
          submissionReference: {
            groupId: pageData.gradingGroupId || pageData.groupId || '',
            itemId: pageData.currentItemId,
            submitterId: getNewSubmitterIdFromLegacyId(routeSubmitterId),
          },
        },
      },
    });
  };

  const onApplyGradeOverrideMutationCompleteV2 = (
    cache: DataProxy,
    { data }: ExecutionResult<ApplyGradeOverrideV2Mutation>
  ) => {
    if (data?.GroupGrading_ApplyGradeOverrideV2) {
      updateCachedAssignmentGradeDataV2(cache, data);
    }
  };

  const [mutateV2, { loading: loadingV2, error: errorV2 }] = useMutation<
    ApplyGradeOverrideV2Mutation,
    ApplyGradeOverrideV2MutationVariables
  >(applyGradeOverrideMutationV2, {
    context: { clientName: 'gatewayGql' },
    update: onApplyGradeOverrideMutationCompleteV2,
  });

  const applyGradeOverrideV2 = ({ grade, explanation, partnerExplanation }: MutationInput) => {
    return mutateV2({
      variables: {
        input: {
          grade,
          explanation: explanation || null,
          partnerExplanation: partnerExplanation || null,
          courseId: pageData.courseId,
          itemId: pageData.currentItemId,
          scopedSubmitterId: getScopedSubmitterIdFromRouteSubmitterId(routeSubmitterId, pageData.gradingGroupId),
        },
      },
    });
  };

  if (useApiV2) {
    return {
      applyGradeOverride: applyGradeOverrideV2,
      loading: loadingV2,
      error: errorV2,
    };
  }
  return {
    applyGradeOverride,
    loading,
    error,
  };
};

export default useApplyGradeOverrideMutation;
